import React, { useState } from 'react';
import { Phone, Clock, Shield, Eye, Award, AlertTriangle, Send, CheckCircle } from 'lucide-react';
import { companyInfo, serviceTypes, securityFeatures } from '../data/mock';

const iconMap = {
  Clock: Clock,
  Shield: Shield,
  AlertTriangle: AlertTriangle,
  Eye: Eye,
  Award: Award
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    service: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
    setFormData({ name: '', email: '', phone: '', city: '', state: '', service: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section className="py-20 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Info */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Security Guard Services <span className="text-orange-500">Company</span>
            </h2>
            
            {/* Testimonials Preview */}
            <div className="space-y-6 mb-8">
              <blockquote className="border-l-4 border-orange-500 pl-4 py-2">
                <p className="text-gray-300 italic">
                  "I've been working with Armed Security Service for over a year now and they're very professional. Easy security company to work with and the security guards are always very alert."
                </p>
              </blockquote>
              <blockquote className="border-l-4 border-orange-500 pl-4 py-2">
                <p className="text-gray-300 italic">
                  "Our construction site needs year round security. Armed Security Service offers the best full service security service we found within our budget."
                </p>
              </blockquote>
              <blockquote className="border-l-4 border-orange-500 pl-4 py-2">
                <p className="text-gray-300 italic">
                  "Working with Armed Security Service has helped our Hotel in so many ways. They're reasonably priced and highly respected. First class security company."
                </p>
              </blockquote>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {securityFeatures.map((feature) => {
                const Icon = iconMap[feature.icon];
                return (
                  <div key={feature.title} className="bg-slate-800 rounded-lg p-4 text-center">
                    <Icon className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                    <p className="text-white text-sm font-medium">{feature.title}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column - Form */}
          <div>
            <div className="bg-slate-800 rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Request a Free Quote</h3>
              
              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-white mb-2">Thank You!</h4>
                  <p className="text-gray-400">We'll get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="city"
                      placeholder="Your City"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                    <input
                      type="text"
                      name="state"
                      placeholder="Your State"
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none cursor-pointer"
                  >
                    <option value="" disabled>Select Service</option>
                    {serviceTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-lg font-bold text-lg transition-all hover:shadow-lg"
                  >
                    <Send className="w-5 h-5" />
                    Submit Request
                  </button>
                </form>
              )}

              {/* Call CTA */}
              <div className="mt-6 pt-6 border-t border-slate-700 text-center">
                <p className="text-gray-400 mb-2">Or call us directly</p>
                <a 
                  href={`tel:${companyInfo.phone.replace(/[^0-9]/g, '')}`}
                  className="inline-flex items-center gap-2 text-2xl font-bold text-orange-500 hover:text-orange-400 transition-colors"
                >
                  <Phone className="w-6 h-6" />
                  {companyInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;